/**
 * App shell navigation (redesign Phase 2) — grouped vertical sidebar on desktop,
 * off-canvas drawer on mobile. Replaces the old flat top-link bar.
 *
 *  - Routes grouped Play / Campaign / Dungeon Master (admin-only, hidden while
 *    impersonating so the DM sees the nav as the player would).
 *  - Active route: elevated background + a 3px accent left bar (survives the
 *    icon-rail collapse, unlike the old bottom-border underline).
 *  - Collapsible to an icon rail on desktop (persisted to localStorage).
 *  - Account footer: avatar, identity, impersonation picker (admin), sign out.
 */
import './AppNav.css';
import { useEffect, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { isAdmin } from '../lib/auth';
import { useIdentity } from '../lib/ImpersonationContext';
import ImpersonationPicker from '../lib/ImpersonationPicker';
import { Icon, IconButton, type IconName } from './ui';
import ThemeMenu from './ThemeMenu';

interface NavEntry {
  to: string;
  icon: IconName;
  label: string;
}
interface NavGroupDef {
  label: string;
  admin?: boolean;
  items: NavEntry[];
}

const GROUPS: NavGroupDef[] = [
  {
    label: 'Play',
    items: [
      { to: '/dashboard', icon: 'dashboard', label: 'Dashboard' },
      { to: '/vtt', icon: 'map', label: 'Battle Map' },
      { to: '/sheet', icon: 'sheet', label: 'Character' },
    ],
  },
  {
    label: 'Campaign',
    items: [
      { to: '/party', icon: 'party', label: 'Party & Loot' },
      { to: '/lore', icon: 'lore', label: 'Lore' },
      { to: '/map', icon: 'world', label: 'World Map' },
      { to: '/levelup', icon: 'levelup', label: 'Level Up' },
      // Convergence is player-facing (PR #2 / convergence-player-access): players
      // can view + edit their own galaxy, so it lives in the player-visible group,
      // not the DM section. Route is ProtectedRoute (any authed user).
      { to: '/convergence', icon: 'convergence', label: 'Convergence' },
    ],
  },
  {
    label: 'Dungeon Master',
    admin: true,
    items: [
      { to: '/audit', icon: 'audit', label: 'Audit' },
      { to: '/admin', icon: 'admin', label: 'Admin' },
    ],
  },
];

export default function AppNav() {
  const { user, signOut } = useAuth();
  const { impersonating } = useIdentity();
  const admin = isAdmin(user);
  const showAdmin = admin && !impersonating;

  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem('gd:nav-collapsed') === '1';
    } catch {
      return false;
    }
  });
  const [drawerOpen, setDrawerOpen] = useState(false);

  const toggleCollapsed = () =>
    setCollapsed((c) => {
      const next = !c;
      try {
        localStorage.setItem('gd:nav-collapsed', next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });

  // Close the mobile drawer on Escape.
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawerOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  if (!user) return null;

  const closeDrawer = () => setDrawerOpen(false);
  const initial = (user.email || user.username || '?').charAt(0).toUpperCase();

  return (
    <>
      {/* Mobile top bar */}
      <header className="app-topbar">
        <IconButton icon="menu" label="Open navigation" variant="ghost" onClick={() => setDrawerOpen(true)} />
        <Link to="/dashboard" className="app-topbar__brand" onClick={closeDrawer}>
          <span className="side__logo">G</span>
          <span className="side__brandtext">GoobDoob</span>
        </Link>
      </header>

      {drawerOpen && <div className="nav-backdrop" onClick={closeDrawer} aria-hidden />}

      <aside
        className={`side${collapsed ? ' side--collapsed' : ''}${drawerOpen ? ' side--open' : ''}`}
        aria-label="Primary"
      >
        <Link to="/dashboard" className="side__brand" onClick={closeDrawer} title="GoobDoob VTT">
          <span className="side__logo">G</span>
          <span className="side__brandtext">GoobDoob</span>
        </Link>

        <nav className="side__nav">
          {GROUPS.filter((g) => !g.admin || showAdmin).map((g) => (
            <div key={g.label}>
              <div className="side__group">{g.label}</div>
              {g.items.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
                  title={collapsed ? it.label : undefined}
                  onClick={closeDrawer}
                >
                  <Icon name={it.icon} size={19} />
                  <span className="nav-item__label">{it.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="side__spacer" />

        <div className="side__footer">
          {admin && (
            <div className="side__impersonation">
              <ImpersonationPicker />
            </div>
          )}
          <div className="side__user">
            <span className="side__avatar">{initial}</span>
            <span className="side__user-meta">
              <span className="side__user-name">{user.email || user.username}</span>
              <span className="side__user-role">{admin ? 'Dungeon Master' : 'Player'}</span>
            </span>
            <ThemeMenu />
            <IconButton icon="logout" label="Sign out" variant="ghost" size="sm" onClick={signOut} />
          </div>
          <button className="side__collapse" onClick={toggleCollapsed} title={collapsed ? 'Expand' : 'Collapse'}>
            <Icon name="chevron" size={16} />
            <span className="nav-item__label">Collapse</span>
          </button>
        </div>
      </aside>
    </>
  );
}
